<template>
    <div class="FeedbackQuestion">
        <p>
            <strong>
                {{ question }}
            </strong>
        </p>
        <div class="mx-4 mt-6">
            <CFormRadio
                v-if="options"
                v-model="answerData.option"
                class="text-left"
                :options="options"
                @update:model-value="updateOption($event)"
            />
            <div v-else-if="!textOnly" class="text-center">
                <FeedbackNpsScale v-model="answerData.nps" @select="updateNps($event)" />
            </div>
        </div>

        <CFormGroup
            v-if="hasNpsAnswer || withCustomMessage || textOnly"
            :class="{
                'ml-8 pr-8': options,
                'mt-6': hasNpsAnswer
            }"
            :label="!withCustomMessage && !textOnly ? placeholder : undefined"
            :label-info="`${getLength(answerData.message)}/700`"
        >
            <CFormTextarea class="mt-2" maxlength="700" :value="answerData.message" @input="updateMessage($event)" />
        </CFormGroup>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { PropType } from 'vue'

import type { optionType } from '#shared/types/dictionary'
import { FeedbackAnswerData, FeedbackOption } from '#shared/types/Feedback'
import { getLength } from '#shared/utils'
import FeedbackNpsScale from '@/components/common/FeedbackNpsScale.vue'

const props = defineProps({
    question: {
        type: String,
        required: true
    },
    placeholder: {
        type: String,
        default: undefined
    },
    options: {
        type: Array as PropType<optionType[]>,
        default: undefined
    },
    textOnly: {
        type: Boolean,
        default: false
    },
    lang: {
        type: String,
        default: 'sk'
    }
})

const emit = defineEmits(['input', 'rate'])

const answerData = ref<FeedbackAnswerData>({
    question: props.question
} as FeedbackAnswerData)

const withCustomMessage = computed((): boolean => {
    return answerData.value.option === FeedbackOption.OTHER_REASON
})

const hasNpsAnswer = computed((): boolean => {
    return answerData.value.nps !== undefined
})

function updateNps(score: number) {
    answerData.value = { ...answerData.value, nps: score }
    emit('input', answerData.value)
    // Persist the score straight away - the comment underneath is optional and the
    // user may well close the modal without ever writing one.
    emit('rate', score)
}

function updateMessage(data: string) {
    answerData.value = { ...answerData.value, message: data }
    emit('input', answerData.value)
}

function updateOption(data: string) {
    let messageData = {}
    if (props.options) {
        Object.values(props.options)
            .filter((value) => value.id !== FeedbackOption.OTHER_REASON)
            .forEach((value) => {
                if (value.id === data) {
                    messageData = { message: value.label }
                }
            })
    }
    
    emit('input', {
        ...answerData.value,
        ...messageData
    })
}
</script>